<template>
  <form @submit.prevent="handleRunQuery" class="uqueries">
    <input v-if="uqueryId" type="hidden" name="uquery_id" :value="uqueryId" />
    <input type="hidden" name="user_accessible_program_ids" :value="userAccessibleProgramIds" />

    <input v-if="refreshUquery" type="hidden" name="refresh_uquery" value="1" />
    <template v-else-if="queryParams">
      <input type="hidden" name="query_params_id" :value="queryParamsId" />
      <input type="hidden" name="query_params_title" :value="queryParamsTitle" />
      <input type="hidden" name="query_params" :value="encodedQueryParams" />
    </template>

    <div class="form-section m-0 mb-6">
      <!-- Title, Saved Query Select, and Favorite -->
      <div class="form-group p-6 w-full lg:w-1/4 lg:max-w-120 shrink-0">
        <div>
          <label for="query-title">Title of query</label>
          <input id="query-title" v-model="formData.queryTitle" type="text" name="query_title" placeholder="Get all users" />
        </div>

        <div>
          <label for="select-query">Select saved query (current form will be reloaded)</label>
          <select id="select-query" v-model="formData.queryId" name="query_id" @change="updateSelectQuery">
            <option value="">-- Select --</option>
            <option v-for="option in currentUqueries" :key="option.value" :value="option.value">
              {{ option.text }}
            </option>
          </select>

          <label class="mt-4">
            <input v-model="formData.favorite" type="checkbox" name="favorite" value="1" /> Favorite
          </label>
        </div>

        <template v-if="adminPermissions.reports">
          <div class="flex justify-between flex-row-reverse">
            <div class="flex space-x-3">
              <button v-if="uqueries.length > 0" type="button" class="secondary button" :disabled="!formData.queryId" @click="handleUpdateQuery">
                Update
              </button>

              <button type="button" class="secondary button left" @click="handleSaveNewQuery">
                Save As New
              </button>
            </div>

            <div>
              <button v-if="uqueries.length > 0" type="button" class="tertiary button" :disabled="!formData.queryId" @click="handleDeleteQuery">
                <i class="far fa-trash-alt"></i>
              </button>
            </div>
          </div>
        </template>
      </div>

      <!-- The meat of the form -->
      <div class="flex flex-col space-y-6 grow" :key="formKey">
        <div>
          <label for="programs">Programs</label>
          <Multiselect
            id="programs"
            name="programs"
            :options="programOptionsForMultiselect"
            :initialSelectedValues="formData.programs"
            @update:modelValue="handleProgramsUpdate"
            placeholder="My programs"
          />
        </div>

        <div>
          <label for="groups">User groups</label>
          <Multiselect
            id="groups"
            name="groups"
            :options="currentGroupOptionsForMultiselect"
            :initialSelectedValues="formData.groups"
            @update:modelValue="handleGroupsUpdate"
            placeholder="All groups"
          />
        </div>

        <div>
          <label for="user_session_statuses">User session statuses</label>
          <Multiselect
            id="user_session_statuses"
            name="user_session_statuses"
            :options="userSessionStatusOptionsForMultiselect"
            :initialSelectedValues="formData.userSessionStatuses"
            @update:modelValue="handleUserSessionStatusesUpdate"
            placeholder="All statuses"
          />
        </div>

        <div class="col-span-2">
          <label for="columns">Data columns</label>
          <Multiselect
            id="columns"
            name="columns"
            :options="currentColumnOptionsForMultiselect"
            :initialSelectedValues="formData.columns"
            @update:modelValue="handleColumnsUpdate"
            placeholder="User's ID, Email, First Name, Last Name"
          />
        </div>

        <hr>

        <!-- Filters -->
        <h3>Filters <small>(constraints on the user pool)</small></h3>
        <button type="button" class="small secondary button w-fit" @click="addFilterBy">Add a Field to filter by</button>

        <div id="filter-by-container" class="by-table flex flex-col space-y-4 mt-4">
          <div v-for="(filter, index) in filterByFields" :key="index" class="flex flex-col space-y-2 bg-gray-100 p-6 rounded-lg">
            <div class="flex space-x-3">
              <div class="w-10">
                <button type="button" class="delete-row button radius transparent" aria-label="Delete row" @click="removeFilterBy(index)">
                  <i class="fa fa-trash-alt"></i>
                </button>
              </div>

              <div class="grow">
                <select v-model="filter.field" :name="`filter_by[${index}]`" @change="updateFilterValues(filter)">
                  <option v-for="option in currentFilterByOptions" :key="option.value" :value="option.value">
                    {{ option.text }}
                  </option>
                </select>
              </div>

              <div class="w-24">
                <label><input v-model="filter.exclude" type="checkbox" :name="`filter_by_exclude[${filter.key}]`" value="1" /> Exclude</label>
              </div>
            </div>

            <div class="w-full">
              <Multiselect
                v-if="filter.values !== undefined && filter.valueOptions"
                :id="`filter_values_${index}`"
                :name="`filter_values[${index}]`"
                :options="filter.valueOptions"
                :initialSelectedValues="filter.values"
                @update:modelValue="(newValues) => updateFilterValuesSelection(index, newValues)"
              />
              <input
                v-else-if="filter.text !== undefined"
                v-model="filter.text"
                type="text"
                :name="`filter_text[${index}]`"
              />
            </div>
          </div>
        </div>

        <hr />

        <!-- Group By -->
        <h3>Group by <small>(aggregate data groupings)</small></h3>
        <button type="button" class="small secondary button w-fit" @click="addGroupBy">
          Add a Field to group by
        </button>

        <div id="group-by-container" class="by-table flex flex-col space-y-4 mt-4">
          <div v-for="(group, index) in groupByFields" :key="index" class="flex flex-col space-y-2 bg-gray-100 p-6 rounded-lg">
            <div class="flex space-x-3">
              <div class="w-10">
                <button type="button" class="delete-row button radius transparent" aria-label="Delete row" @click="removeGroupBy(index)">
                  <i class="fa fa-trash-alt"></i>
                </button>
              </div>

              <div class="grow">
                <select v-model="group.field" :name="`group_by[${index}]`" @change="updateGroupValues(group)">
                  <option v-for="option in currentGroupByOptions" :key="option.value" :value="option.value">
                    {{ option.text }}
                  </option>
                </select>
              </div>
            </div>

            <div class="w-full">
              <Multiselect
                v-if="group.values !== undefined && group.valueOptions"
                :id="`group_values_${index}`"
                :name="`group_values[${index}]`"
                :options="group.valueOptions"
                :initialSelectedValues="group.values"
                @update:modelValue="(newValues) => updateGroupValuesSelection(index, newValues)"
              />
              <input
                v-else-if="group.text !== undefined"
                v-model="group.text"
                type="text"
                :name="`group_text[${index}]`"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Additional Options -->
      <div class="bg-gray-100 p-6 w-full lg:w-1/4 lg:max-w-120 rounded">
        <h2>Additional Options</h2>

        <div class="flex flex-col space-y-4">
          <label>
            <input id="restrict_columns_to_groups" v-model="formData.restrictColumnsToGroups" type="checkbox" name="restrict_columns_to_groups" value="1" @change="handleRestrictColumnsToGroupsChange" />
            Restrict columns to current profile forms of selected groups
          </label>

          <label>
            <input v-model="formData.includeOnlyProfiles" type="checkbox" name="include_only_profiles" value="1" />
            Include only users with profiles
            <InlineHelp :force-right="true"><template #content>The default is to include all users. Check this box to limit the query to only people with profiles.</template></InlineHelp>
          </label>

          <label>
            <input v-model="formData.activeUsersOnly" type="checkbox" name="active_users_only" value="1" />
            Exclude all disabled user accounts
            <InlineHelp :force-right="true"><template #content>This has nothing to do with the activeness of a user's participation in a session. It is only excluding users whose accounts under "All Accounts" have been disabled by using the eye icon.</template></InlineHelp>
          </label>

          <label>
            <input v-model="formData.separateColumns" type="checkbox" name="separate_columns" value="1" />
            Separate columns
            <InlineHelp :force-right="true"><template #content>For n-groups, the default is to include all the entries for a subfield in the same cell. Check this box to separate them into columns for easy exporting.</template></InlineHelp>
          </label>

          <label>
            <input v-model="formData.excludePrivate" type="checkbox" name="exclude_private" value="1" />
            Exclude all private user answers
            <InlineHelp :force-right="true"><template #content>In displaying user answers, the default is to include private answers. Check this box to exclude answers users marked as private.</template></InlineHelp>
          </label>

          <label>
            <input v-model="formData.separateGroupBys" type="checkbox" name="separate_group_bys" value="1" />
            Separate Group Bys
            <InlineHelp :force-right="true"><template #content>When there are multiple group by fields, the default is to group by all the fields at once (so each combination of Group By field values would be its own category). Check this box if you want each Group By to be a separate aggregate report.</template></InlineHelp>
          </label>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex flex-row-reverse space-between w-full">
      <div>
        <button type="submit" class="button">Run Query</button>
      </div>
    </div>
  </form>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import InlineHelp from "./InlineHelp.vue"
import Multiselect from './Multiselect.vue'


// Props
const props = defineProps({
  uqueries:                 { type: Array,    required: true },
  programOptions:           { type: Array,    required: true },
  groupOptions:             { type: Array,    required: true },
  columnOptions:            { type: Array,    required: true },
  userSessionStatusOptions: { type: Array,    required: true },
  adminPermissions:         { type: Object,   required: true },
  uqueryId:                 { type: String,   default: null },
  userAccessibleProgramIds: { type: String,   required: true },
  refreshUquery:            { type: Boolean,  default: false },
  queryParams:              { type: Object,   default: null },
  queryParamsId:            { type: String,   default: null },
  queryParamsTitle:         { type: String,   default: null }
})

// Form data
const formData = ref({
  queryTitle: '',
  queryId: '',
  favorite: false,
  programs: [],
  groups: [],
  columns: [],
  restrictColumnsToGroups: true,
  includeOnlyProfiles: false,
  activeUsersOnly: true,
  separateColumns: false,
  excludePrivate: false,
  separateGroupBys: false,
  userSessionStatuses: []
})

const formKey = ref(0)

// Dynamic fields
const filterByFields = ref([])
const groupByFields = ref([])

// Dynamic options
const currentGroupOptions = ref([...props.groupOptions])
const currentColumnOptions = ref([...props.columnOptions])
const currentFilterByOptions = ref([])
const currentGroupByOptions = ref([])
const currentUqueries = ref([...props.uqueries])

// Computed
const encodedQueryParams = computed(() => {
  return props.queryParams ? encodeURIComponent(JSON.stringify(props.queryParams)) : ''
})

// Computed properties for multiselect format
const programOptionsForMultiselect = computed(() =>
  props.programOptions.map(([value, text]) => ({ value: String(value), text }))
)

const currentGroupOptionsForMultiselect = computed(() =>
  currentGroupOptions.value.map(group => ({ value: String(group.id), text: group.name }))
)

const currentColumnOptionsForMultiselect = computed(() =>
  currentColumnOptions.value
)

const userSessionStatusOptionsForMultiselect = computed(() =>
  props.userSessionStatusOptions.map(([value, text]) => ({ value: String(value), text }))
)

// Update handlers for multiselects
const handleProgramsUpdate = (newValues) => {
  formData.value.programs = newValues.map(item => item.value)
  handleProgramChange()
}

const handleGroupsUpdate = (newValues) => {
  formData.value.groups = newValues.map(item => item.value)
  handleGroupsChange()
}

const handleColumnsUpdate = (newValues) => {
  formData.value.columns = newValues.map(item => item.value)
}

const handleUserSessionStatusesUpdate = (newValues) => {
  formData.value.userSessionStatuses = newValues.map(item => item.value)
}

const updateFilterValuesSelection = (index, newValues) => {
  filterByFields.value[index].values = newValues.map(item => item.value)
}

const updateGroupValuesSelection = (index, newValues) => {
  groupByFields.value[index].values = newValues.map(item => item.value)
}


// Methods
const updateSelectQuery = async () => {
  if (!formData.value.queryId) {
    clearForm()
    return
  }

  try {
    const params = new URLSearchParams({ query_id: formData.value.queryId })
    const response = await fetch(`/admin/reports/uqueries/get?${params}`)
    const data = await response.json()

    const titlesResponse = await fetch('/admin/reports/uqueries/titles')
    const titlesData = await titlesResponse.json()
    const title = titlesData[formData.value.queryId]

    loadForm(formData.value.queryId, title, data)
  } catch (error) {
    console.error('Error loading query:', error)
  }
}

const clearForm = () => {
  formData.value = {
    queryTitle: '',
    queryId: '',
    favorite: false,
    programs: [],
    groups: [],
    columns: [],
    restrictColumnsToGroups: true,
    includeOnlyProfiles: false,
    activeUsersOnly: true,
    separateColumns: false,
    excludePrivate: false,
    separateGroupBys: false,
    userSessionStatuses: []
  }
  filterByFields.value = []
  groupByFields.value = []
}

const loadForm = async (queryId, title, data) => {
  clearForm()

  formData.value.queryTitle = title.replace(/\s*(\([^\(\)]+\))$/gm, "")
  formData.value.queryId = queryId
  formData.value.favorite = data.favorite === '1'
  formData.value.includeOnlyProfiles = data.include_only_profiles === '1'
  formData.value.activeUsersOnly = data.active_users_only === '1'
  formData.value.separateColumns = data.separate_columns === '1'
  formData.value.restrictColumnsToGroups = data.restrict_columns_to_groups === '1'
  formData.value.excludePrivate = data.exclude_private === '1'
  formData.value.separateGroupBys = data.separate_group_bys === '1'

  formData.value.programs = data.programs || []
  formData.value.groups = data.groups || []
  formData.value.columns = data.columns || []
  formData.value.userSessionStatuses = data.user_session_statuses || []

  await handleProgramChange()

  // Load filter by fields
  if (data.filter_by) {
    filterByFields.value = []
    for (const [key, value] of Object.entries(data.filter_by)) {
      const filter = {
        key: key,
        field: value,
        values: data.filter_values?.[key] || [],
        text: data.filter_text?.[key] || '',
        exclude: data.filter_by_exclude?.[key] === '1',
        valueOptions: []
      }
      filterByFields.value.push(filter)
      await updateFilterValues(filter, true)
    }
  }

  // Load group by fields
  if (data.group_by) {
    groupByFields.value = []
    for (const [key, value] of Object.entries(data.group_by)) {
      const groupBy = {
        key: key,
        field: value,
        values: data.group_values?.[key] || [],
        text: data.group_text?.[key] || '',
        valueOptions: []
      }
      groupByFields.value.push(groupBy)
      await updateGroupValues(groupBy, true)
    }
  }

  // This forces a rerender
  formKey.value++
}

const handleProgramChange = async () => {
  await updateGroups()
  await updateDataColumns()
  await updateFilterAndGroupBySelects()
}

const handleGroupsChange = async () => {
  await updateDataColumns()
  await updateFilterAndGroupBySelects()
}

const handleRestrictColumnsToGroupsChange = async () => {
  await handleGroupsChange()
}

const updateGroups = async () => {
  const programIds = formData.value.programs.length > 0
    ? formData.value.programs
    : props.userAccessibleProgramIds.split(',')

  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const response = await fetch('/admin/select_options', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        inputs: { programs: programIds },
        output_select_ids: ['groups'],
        dependency_tree: { programs: '["groups"]' }
      })
    })

    const data = await response.json()

    currentGroupOptions.value = data.groups.map(([id, name]) => ({ id, name }))

    // Keep only valid groups
    formData.value.groups = formData.value.groups.filter(groupId =>
      currentGroupOptions.value.some(g => String(g.id) === groupId)
    )
  } catch (error) {
    console.error('Error updating groups:', error)
  }
}

const updateDataColumns = async () => {
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    const params = new URLSearchParams({
      restrict_columns_to_groups: formData.value.restrictColumnsToGroups
    })

    formData.value.programs.forEach(id => {
      params.append('program_ids[]', id)
    })

    formData.value.groups.forEach(id => {
      params.append('group_ids[]', id)
    })

    const response = await fetch(`/admin/reports/uqueries/data_columns?${params}`)
    currentColumnOptions.value = await response.json()
  } catch (error) {
    console.error('Error updating columns:', error)
  }
}

const updateFilterAndGroupBySelects = async () => {
  try {
    // Update filter options
    const filterParams = new URLSearchParams({
      program_ids: formData.value.programs,
      group_ids: formData.value.groups,
      restrict_columns_to_groups: formData.value.restrictColumnsToGroups,
      include_keyword: true
    })
    const filterResponse = await fetch(`/admin/reports/uqueries/fields?${filterParams}`)
    currentFilterByOptions.value = await filterResponse.json()

    // Update group options
    const groupParams = new URLSearchParams({
      program_ids: formData.value.programs,
      group_ids: formData.value.groups,
      restrict_columns_to_groups: formData.value.restrictColumnsToGroups,
      include_keyword: false
    })
    const groupResponse = await fetch(`/admin/reports/uqueries/fields?${groupParams}`)
    currentGroupByOptions.value = await groupResponse.json()
  } catch (error) {
    console.error('Error updating field options:', error)
  }
}

const addFilterBy = () => {
  filterByFields.value.push({
    field: '',
    values: [],
    text: '',
    exclude: false,
    valueOptions: []
  })
}

const removeFilterBy = (index) => {
  filterByFields.value.splice(index, 1)
}

const addGroupBy = () => {
  groupByFields.value.push({
    field: '',
    values: [],
    text: '',
    valueOptions: []
  })
}

const removeGroupBy = (index) => {
  groupByFields.value.splice(index, 1)
}

const updateFilterValues = async (filter, preserve = false) => {
  if (!filter.field) return

  try {
    const params = new URLSearchParams({ field_name: filter.field })
    const response = await fetch(`/admin/programs/fields/list_values?${params}`)
    const data = await response.json()

    // If data is empty, it's a text field
    if (data.length == 0) {
      filter.valueOptions = null
      if (!preserve) {
        filter.values = undefined
        filter.text = ''
      }
    } else {
      filter.valueOptions = data
      if (!preserve) {
        filter.values = []
        filter.text = undefined
      }
      pushOptionsToMultiselect(`filter_values_${filterByFields.value.indexOf(filter)}`, data)
    }
  } catch (error) {
    console.error('Error updating filter values:', error)
  }
}

// The value Multiselect snapshots its options at mount and only refreshes them via
// a setOptions event, so hand it the freshly-fetched options after a field change.
const pushOptionsToMultiselect = (id, options) => {
  nextTick(() => {
    document.getElementById(id)?.dispatchEvent(new CustomEvent('setOptions', { detail: options }))
  })
}

const updateGroupValues = async (group, preserve = false) => {
  if (!group.field) return

  try {
    const params = new URLSearchParams({ field_name: group.field })
    const response = await fetch(`/admin/programs/fields/list_values?${params}`)
    const data = await response.json()

    // If data is empty, it's a text field
    if (data.length == 0) {
      group.valueOptions = null
      if (!preserve) {
        group.values = undefined
        group.text = ''
      }
    } else {
      group.valueOptions = data
      if (!preserve) {
        group.values = []
        group.text = undefined
      }
      pushOptionsToMultiselect(`group_values_${groupByFields.value.indexOf(group)}`, data)
    }
  } catch (error) {
    console.error('Error updating group values:', error)
  }
}

const handleRunQuery = async () => {
  const resultsElement = document.getElementById('results')
  resultsElement.innerHTML = '<div class="muted center padded-3em note large-text"><i><i title="Working" class="fa fa-spinner pulse fas"></i> Loading...</i></div>'

  const formElement = document.querySelector('.uqueries')
  const formDataToSend = new FormData(formElement)

  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const response = await fetch('/admin/reports/uqueries/run', {
      method: 'POST',
      headers: {
        'Accept': 'text/javascript',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': csrfToken,
      },
      body: formDataToSend
    })

    const jsCode = await response.text();
    eval(jsCode);
  } catch (error) {
    console.error('Error running query:', error)
  }
}

const handleUpdateQuery = async () => {
  const formElement = document.querySelector('.uqueries')
  const formDataToSend = new FormData(formElement)

  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const response = await fetch('/admin/reports/uqueries/update_query', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: formDataToSend
    })

    window.display_alert(response)
    currentUqueries.value = await response.json()
  } catch (error) {
    console.error('Error updating query:', error)
  }
}

const handleSaveNewQuery = async () => {
  const formElement = document.querySelector('.uqueries')
  const formDataToSend = new FormData(formElement)

  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const response = await fetch('/admin/reports/uqueries', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: formDataToSend
    })

    window.display_alert(response)
    const data = await response.json()

    currentUqueries.value = data
    // Whenever you create a new query, the one you created should have the biggest ID
    formData.value.queryId = Math.max(...currentUqueries.value.map(obj => obj.value))
  } catch (error) {
    console.error('Error saving query:', error)
  }
}

const handleDeleteQuery = async () => {
  if (!confirm('This will delete the uquery for you and all other admins. Are you sure?')) {
    return
  }

  const formElement = document.querySelector('.uqueries')
  const formDataToSend = new FormData(formElement)

  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const response = await fetch('/admin/reports/uqueries/delete_query', {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: formDataToSend
    })

    window.display_alert(response)
    const data = await response.json()

    currentUqueries.value = data
    clearForm()
  } catch (error) {
    console.error('Error deleting query:', error)
  }
}

// Lifecycle
onMounted(async () => {
  let loadedQuery = false

  if (props.uqueryId) {
    formData.value.queryId = props.uqueryId
    if (location.hash.includes('reload')) {
      await updateSelectQuery()
      loadedQuery = true
    }
  }

  if (props.refreshUquery) {
    await updateSelectQuery()
    loadedQuery = true
  }

  if (props.queryParams) {
    await loadForm(props.queryParamsId, props.queryParamsTitle, props.queryParams)
    loadedQuery = true
  }

  // On a fresh form (no saved query loaded) populate the field/group dropdowns for the
  // default "all my programs" selection, otherwise "Add a Field to filter by" is empty.
  if (!loadedQuery) {
    await handleProgramChange()
  }
})
</script>
